export const OUTCOMES = ['answered', 'no_answer', 'voicemail', 'failed', 'pending'] as const;
export type Outcome = (typeof OUTCOMES)[number];

export const OUTCOME_LABELS: Record<Outcome, string> = {
  answered: 'Answered',
  no_answer: 'No answer',
  voicemail: 'Voicemail',
  failed: 'Failed',
  pending: 'In progress',
};

/** CSS custom properties defined in styles.css — validated as a set, in this order. */
export const OUTCOME_COLORS: Record<Outcome, string> = {
  answered: 'var(--outcome-answered)',
  no_answer: 'var(--outcome-no_answer)',
  voicemail: 'var(--outcome-voicemail)',
  failed: 'var(--outcome-failed)',
  pending: 'var(--outcome-pending)',
};

export interface AuthStatus {
  signedIn: boolean;
  passwordRequired: boolean;
  connected: boolean;
  /** True when the app is showing seeded demo data with no HighLevel install. */
  demoMode: boolean;
  /** Listing and creating sub-accounts need agency-level credentials. */
  hasAgencyCredentials: boolean;
  privateTokenCount: number;
  /** Whether GHL_CLIENT_ID/SECRET/REDIRECT_URI are set, enabling the OAuth flow. */
  oauthConfigured: boolean;
  companyId: string | null;
  scopes: string[];
  connectedAt: number | null;
  locationCount: number;
  installUrl: string;
  /** Named logins exist, so the sign-in form asks for an email. */
  namedUsers: boolean;
  /** The setup password still works (no named login exists yet). */
  sharedPassword: boolean;
  timeZones: TimeZoneSettings;
  user: CurrentUser | null;
}

export type Role = 'owner' | 'admin' | 'member' | 'viewer';
export type Area = 'contacts' | 'messaging' | 'opportunities' | 'calendars' | 'workflows';
export type Level = 'off' | 'view' | 'act';

export type TabId =
  | 'overview'
  | 'calls'
  | 'callLog'
  | 'appointments'
  | 'opportunities'
  | 'assistant'
  | 'subaccounts'
  | 'files'
  | 'logs'
  | 'sync'
  | 'settings';

export type TabLevel = 'off' | 'view' | 'edit';

export interface CurrentUser {
  id: string;
  name: string;
  email: string | null;
  role: Role;
  shared: boolean;
  permissions: Record<Area, Level>;
  tabs: Record<TabId, TabLevel>;
  locationIds: string[] | null;
  canManageUsers: boolean;
  canManageSettings: boolean;
}

export interface DashboardUser {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  permissions: Record<Area, Level>;
  tabs: Record<TabId, TabLevel>;
  locationIds: string[] | null;
  disabled: boolean;
  createdAt: number;
  lastLoginAt: number | null;
}

export interface UsersResponse {
  users: DashboardUser[];
  roles: Role[];
  areas: { id: Area; label: string; view: string; act: string }[];
  defaults: Record<Role, Record<Area, Level>>;
  tabs: { id: TabId; label: string; view: string; edit: string | null }[];
  tabDefaults: Record<Role, Record<TabId, TabLevel>>;
}

export interface GhlAppStatus {
  source: 'env' | 'settings' | 'none';
  fromEnv: boolean;
  clientId: string;
  secretHint: string | null;
  redirectUri: string;
  scopes: string[];
  recommendedScopes: { read: string[]; write: string[] };
  installed: boolean;
  installedAt: number | null;
  companyId: string | null;
  grantedScopes: string[];
}

// --- Assistant -------------------------------------------------------------
export type AssistantItem =
  | { kind: 'user'; id: number; text: string; at: number }
  | { kind: 'assistant'; id: number; text: string; at: number }
  | { kind: 'lookup'; id: string; tools: string[] }
  | {
      kind: 'action';
      id: string;
      tool: string;
      summary: string;
      status: 'pending' | 'rejected' | 'done' | 'failed';
      error: string | null;
      locationName: string | null;
      decidedAt: number | null;
    };

export interface AssistantConversation {
  id: string;
  title: string | null;
  locationId: string | null;
  pending: boolean;
  items: AssistantItem[];
}

export interface AssistantOverview {
  ready: boolean;
  conversations: { id: string; title: string | null; locationId: string | null; updatedAt: number; pending: number }[];
  capabilities: { area: Area; label: string; level: Level }[];
  tools: string[];
}

export interface LocationSummary {
  id: string;
  name: string;
  /** Has its own AI business description / rating prompt (Sub-accounts → AI setup). */
  aiSetup: { context: boolean; prompt: boolean };
  city: string | null;
  state: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  timezone: string | null;
  trackingEnabled: boolean;
  /** Marked as a live client; the filters can narrow to these. */
  isActiveClient: boolean;
  /** Whether a credential exists that can actually read this sub-account. */
  connected: boolean;
  totalCalls: number;
  answeredCalls: number;
  lastCallAt: number | null;
  noteCount: number;
  lastSyncAt: number | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
}

export interface CallRecord {
  id: string;
  locationId: string;
  locationName: string | null;
  contactId: string | null;
  contactName: string | null;
  leadScore: LeadScore | null;
  contactPhone: string | null;
  contactUrl: string | null;
  conversationUrl: string | null;
  userId: string | null;
  userName: string | null;
  direction: string | null;
  messageType: string | null;
  callStatus: string | null;
  outcome: Outcome;
  durationSeconds: number;
  source: string | null;
  dateAdded: number;
}

export interface CallsResponse {
  calls: CallRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export type OutcomeCounts = Record<Outcome, number>;

export interface TimeseriesPoint extends OutcomeCounts {
  day: string;
  total: number;
}

export interface LocationBreakdown extends OutcomeCounts {
  locationId: string;
  locationName: string | null;
  total: number;
  talkSeconds: number;
  callers: number;
}

export interface UserBreakdown {
  userId: string | null;
  userName: string | null;
  locationId: string;
  locationName: string | null;
  total: number;
  answered: number;
  no_answer: number;
  voicemail: number;
  failed: number;
  talkSeconds: number;
}

export interface SummaryResponse {
  range: { from: number; to: number };
  totals: {
    calls: number;
    answered: number;
    noAnswer: number;
    voicemail: number;
    failed: number;
    pending: number;
    inbound: number;
    outbound: number;
    uniqueContacts: number;
    talkSeconds: number;
    answeredRate: number;
    avgAnsweredDurationSeconds: number;
  };
  previous: { calls: number; answered: number; talkSeconds: number; answeredRate: number };
  timeseries: TimeseriesPoint[];
  byLocation: LocationBreakdown[];
  byUser: UserBreakdown[];
}

export interface Note {
  id: string;
  locationId: string;
  title: string | null;
  body: string;
  author: string | null;
  pinned: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface SyncStatus {
  running: boolean;
  /** Contact names and recording copies being filled in after the data landed. */
  enriching: boolean;
  /** Changes whenever the sync stored something new; reports re-fetch on change. */
  dataVersion: number;
  progress: { startedAt: number; total: number; done: number; active: string[] } | null;
  enabled: boolean;
  demoMode: boolean;
  intervalMinutes: number;
  fullIntervalMinutes: number;
  concurrency: number;
  pendingContacts: number;
  recordings: { stored: number; bytes: number; missing: number; failing: number; deleted: number; waiting: number };
  lastSummary: {
    startedAt: number;
    finishedAt: number;
    lane: 'full' | 'fast' | 'mixed';
    locations: number;
    calls: number;
    appointments: number;
    opportunities: number;
    opportunitySweepsSkipped: number;
    activity: number;
    contacts: number;
    recordings: number;
    skipped: { locationId: string; name: string | null }[];
    errors: { locationId: string; message: string }[];
  } | null;
  perLocation: {
    locationId: string;
    locationName: string | null;
    lastRunAt: number | null;
    lastSuccessAt: number | null;
    lastStatus: string | null;
    lastError: string | null;
    lastCallAt: number | null;
    callsStored: number;
  }[];
}

export interface CallFilterState {
  from: string;
  to: string;
  locationIds: string[];
  userIds: string[];
  outcomes: Outcome[];
  direction: '' | 'inbound' | 'outbound';
  search: string;
  /** Narrow every figure to sub-accounts marked as active clients. */
  activeClientsOnly: boolean;
  /** Contact tags to narrow to. */
  tags: string[];
  /** `any` matches a contact with any listed tag; `all` requires every one. */
  tagMatch: 'any' | 'all';
}

// ---------------------------------------------------------------------------
// Appointments
// ---------------------------------------------------------------------------

export const APPOINTMENT_OUTCOMES = ['showed', 'noshow', 'booked', 'cancelled', 'invalid'] as const;
export type AppointmentOutcome = (typeof APPOINTMENT_OUTCOMES)[number];

export const APPOINTMENT_OUTCOME_LABELS: Record<AppointmentOutcome, string> = {
  showed: 'Showed',
  noshow: 'No show',
  booked: 'Booked',
  cancelled: 'Cancelled',
  invalid: 'Invalid',
};

/**
 * The same validated hue order as the call outcomes — green, amber, blue, red,
 * plus a neutral for the residual bucket. Re-run the palette validator before
 * changing a colour or the stack order.
 */
export const APPOINTMENT_OUTCOME_COLORS: Record<AppointmentOutcome, string> = {
  showed: 'var(--outcome-answered)',
  noshow: 'var(--outcome-no_answer)',
  booked: 'var(--outcome-voicemail)',
  cancelled: 'var(--outcome-failed)',
  invalid: 'var(--outcome-pending)',
};

export interface AppointmentRecord {
  id: string;
  locationId: string;
  locationName: string | null;
  calendarId: string | null;
  calendarName: string | null;
  contactId: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactUrl: string | null;
  calendarUrl: string;
  title: string | null;
  appointmentStatus: string | null;
  outcome: AppointmentOutcome;
  startTime: number;
  endTime: number | null;
  bookedAt: number | null;
  durationMinutes: number;
  isRecurring: boolean;
  deletedAt: number | null;
  bookedByUserId: string | null;
  bookedByName: string | null;
  bookedBySource: string | null;
  assignedUserId: string | null;
  assignedName: string | null;
}

export interface AppointmentsResponse {
  appointments: AppointmentRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export type AppointmentCounts = Record<AppointmentOutcome, number>;

export interface AppointmentTimeseriesPoint extends AppointmentCounts {
  day: string;
  total: number;
}

export interface AppointmentLocationBreakdown extends AppointmentCounts {
  locationId: string;
  locationName: string | null;
  total: number;
  calendars: number;
}

export interface AppointmentUserBreakdown {
  userId: string | null;
  userName: string | null;
  source: string | null;
  locationId: string;
  locationName: string | null;
  total: number;
  showed: number;
  noshow: number;
  cancelled: number;
}

export interface AppointmentCalendarBreakdown {
  calendarId: string | null;
  calendarName: string | null;
  locationId: string;
  locationName: string | null;
  total: number;
  showed: number;
  noshow: number;
}

export const APPOINTMENT_EVENT_LABELS: Record<string, string> = {
  created: 'Booked',
  rescheduled: 'Rescheduled',
  status_changed: 'Status changed',
  assigned_changed: 'Reassigned',
  calendar_changed: 'Moved calendar',
  deleted: 'Deleted',
  activity: 'Activity',
};

export interface AppointmentJourneyResponse {
  appointment: AppointmentRecord;
  timeline: JourneyEvent[];
  attributed: number;
  reschedules: number;
  /** False when the contact has other appointments, so unmatched activity is withheld. */
  showsAllActivity: boolean;
}

export interface AppointmentSummaryResponse {
  range: { from: number; to: number };
  dateBasis: 'start' | 'booked';
  userBasis: 'booker' | 'assigned';
  totals: {
    appointments: number;
    showed: number;
    noshow: number;
    booked: number;
    cancelled: number;
    invalid: number;
    resolved: number;
    showRate: number;
    cancelRate: number;
    bookedMinutes: number;
    uniqueContacts: number;
    calendars: number;
    selfBooked: number;
  };
  previous: { appointments: number; showed: number; showRate: number };
  timeseries: AppointmentTimeseriesPoint[];
  byLocation: AppointmentLocationBreakdown[];
  byUser: AppointmentUserBreakdown[];
  byCalendar: AppointmentCalendarBreakdown[];
  /** Recorded changes in the window — reschedules, cancellations, reassignments. */
  movement: { event_type: string; count: number }[];
}

export interface AppointmentFilterState {
  from: string;
  to: string;
  locationIds: string[];
  userIds: string[];
  calendarIds: string[];
  outcomes: AppointmentOutcome[];
  search: string;
  activeClientsOnly: boolean;
  tags: string[];
  tagMatch: 'any' | 'all';
  /** `start` = when it happens, `booked` = when it was booked. */
  dateBasis: 'start' | 'booked';
  /** Which person the user filter and the per-user report mean. */
  userBasis: 'booker' | 'assigned';
}

export interface Connection {
  id: string;
  kind: 'location' | 'agency';
  locationId: string | null;
  locationName?: string | null;
  companyId: string | null;
  label: string | null;
  /** Last four characters only — the token itself never leaves the server. */
  hint: string;
  createdAt: number;
  lastVerifiedAt: number | null;
  lastError: string | null;
}

export interface ConnectionsResponse {
  tokens: Connection[];
  oauthInstall: { companyId: string; scopes: string[]; connectedAt: number } | null;
  hasAgencyCredentials: boolean;
}

export interface ContactResolutionStatus {
  counts: { pending: number; ok: number; missing: number };
  /** Distinct failure reasons, commonest first. */
  errors: { last_error: string; count: number }[];
}

// ---------------------------------------------------------------------------
// Opportunities
// ---------------------------------------------------------------------------

export const OPPORTUNITY_STATUSES = ['won', 'abandoned', 'open', 'lost'] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];

export const OPPORTUNITY_STATUS_LABELS: Record<OpportunityStatus, string> = {
  won: 'Won',
  abandoned: 'Abandoned',
  open: 'Open',
  lost: 'Lost',
};

/** The same validated hue order as the call and appointment stacks. */
export const OPPORTUNITY_STATUS_COLORS: Record<OpportunityStatus, string> = {
  won: 'var(--outcome-answered)',
  abandoned: 'var(--outcome-no_answer)',
  open: 'var(--outcome-voicemail)',
  lost: 'var(--outcome-failed)',
};

export const OPPORTUNITY_EVENT_LABELS: Record<string, string> = {
  created: 'Created',
  stage_changed: 'Moved stage',
  status_changed: 'Status changed',
  value_changed: 'Value changed',
  assigned_changed: 'Reassigned',
  deleted: 'Deleted',
};

export interface OpportunityRecord {
  id: string;
  locationId: string;
  locationName: string | null;
  contactId: string | null;
  contactName: string | null;
  leadScore: LeadScore | null;
  contactPhone: string | null;
  contactUrl: string | null;
  opportunityUrl: string;
  pipelineId: string | null;
  pipelineName: string | null;
  stageId: string | null;
  stageName: string | null;
  assignedUserId: string | null;
  assignedName: string | null;
  name: string | null;
  status: OpportunityStatus;
  source: string | null;
  monetaryValue: number;
  createdAt: number | null;
  updatedAt: number | null;
  lastStageChangeAt: number | null;
  lastStatusChangeAt: number | null;
  deletedAt: number | null;
  /** The contact's most recent past appointment. */
  lastAppointmentAt: number | null;
  /** The contact's soonest future appointment. */
  nextAppointmentAt: number | null;
  /** When an appointment was most recently booked for this contact. */
  lastAppointmentBookedAt: number | null;
  appointmentCount: number;
  lastAppointmentOutcome: AppointmentOutcome | null;
  lastAppointmentTitle: string | null;
  /**
   * True when the contact has other opportunities — HighLevel links
   * appointments to contacts, not deals, so these facts are shared.
   */
  appointmentsSharedWithSiblings: boolean;
}

export interface OpportunitiesResponse {
  opportunities: OpportunityRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface JourneyEvent {
  id: string;
  /** `transition` = a field change; `activity` = HighLevel's own audit entry. */
  kind: 'transition' | 'activity';
  type: string;
  occurredAt: number;
  from: string | null;
  to: string | null;
  actorUserId: string | null;
  actorName: string | null;
  /**
   * Where the person came from: `event` if HighLevel put them on the change
   * itself, `activity` if matched from a conversation activity entry.
   */
  actorSource: 'event' | 'activity' | null;
  detectedBy: string;
  /** HighLevel's own wording, on activity entries. */
  body: string | null;
  msUntilNext: number | null;
}

export interface JourneyResponse {
  opportunity: OpportunityRecord;
  timeline: JourneyEvent[];
  attributed: number;
  /** False when the contact has other opportunities, so unmatched activity is withheld. */
  showsAllActivity: boolean;
}

// ---------------------------------------------------------------------------
// Contact journey
// ---------------------------------------------------------------------------

export type TimelineKind = 'call' | 'appointment' | 'opportunity' | 'message' | 'activity';

export interface ContactTimelineEntry {
  kind: TimelineKind;
  id: string;
  occurredAt: number;
  title: string | null;
  detail: string | null;
  actorUserId: string | null;
  actorName: string | null;
  meta: string | number | null;
}

export interface ContactJourneyResponse {
  contact: {
    id: string;
    locationId: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    companyName: string | null;
    url: string;
    tags: string[];
    leadScore: LeadScore | null;
  };
  opportunities: { id: string; name: string | null; status: string; monetaryValue: number }[];
  timeline: ContactTimelineEntry[];
}

export type OpportunityCounts = Record<OpportunityStatus, number>;

export interface OpportunityTimeseriesPoint extends OpportunityCounts {
  day: string;
  total: number;
}

export interface OpportunitySummaryResponse {
  range: { from: number; to: number };
  totals: {
    opportunities: number;
    won: number;
    lost: number;
    open: number;
    abandoned: number;
    deleted: number;
    resolved: number;
    winRate: number;
    totalValue: number;
    wonValue: number;
    openValue: number;
    uniqueContacts: number;
    withAppointments: number;
    withUpcomingAppointments: number;
    withoutAppointments: number;
  };
  previous: { opportunities: number; won: number; wonValue: number };
  timeseries: OpportunityTimeseriesPoint[];
  byStage: {
    /** Comma-separated ids: one stage name spans a sub-account's worth of ids. */
    stageIds: string | null;
    stageName: string | null;
    pipelineName: string | null;
    position: number;
    total: number;
    subAccounts: number;
    value: number;
    won: number;
  }[];
  byLocation: {
    locationId: string;
    locationName: string | null;
    total: number;
    won: number;
    lost: number;
    open: number;
    abandoned: number;
    value: number;
  }[];
  byUser: {
    userId: string | null;
    userName: string | null;
    locationId: string;
    locationName: string | null;
    total: number;
    won: number;
    lost: number;
    value: number;
  }[];
  activity: { event_type: string; count: number }[];
}

export interface OpportunityFilterState {
  from: string;
  to: string;
  locationIds: string[];
  userIds: string[];
  statuses: OpportunityStatus[];
  pipelineIds: string[];
  stageIds: string[];
  search: string;
  activeClientsOnly: boolean;
  tags: string[];
  tagMatch: 'any' | 'all';
  includeDeleted: boolean;
  /** Narrow to opportunities whose contact has an appointment in this window. */
  appointmentFrom: string;
  appointmentTo: string;
  appointmentState: 'any' | 'has' | 'none' | 'upcoming' | 'past';
  sort: 'created' | 'lastAppointment' | 'nextAppointment' | 'value' | 'leadScore';
  /** '' for any; otherwise the minimum lead rating, 0-10. */
  minLeadScore: string;
}

export interface LeadScoreDetail {
  key: string;
  label: string;
  max: number;
  points: number;
  answer: string | null;
  reading: string | null;
  status: 'scored' | 'unanswered' | 'unreadable';
}

/** The DSCR form-answer score out of 10, with how each answer scored. */
export interface FormLeadScore {
  score: number;
  answered: number;
  questions: number;
  detail: LeadScoreDetail[];
}

/** The lead rating shown everywhere: the AI rating out of 10. `form` holds what the lead answered. */
export interface LeadScore {
  score: number;
  source: 'ai';
  reason: string | null;
  temperature: string | null;
  confidence: string | null;
  ratedAt: number | null;
  /** 'batch' (background rating) or 'summary' (set by a lead summary). */
  ratedFrom: string | null;
  form: FormLeadScore | null;
}

export interface RatingStatus {
  enabled: boolean;
  windowDays: number;
  dailyLimit: number;
  model: string;
  prompt: string;
  /** The built-in prompt, used while `prompt` is empty. */
  defaultPrompt: string;
  pushToGhl: boolean;
  /** Writing ratings to HighLevel's "Lead Rating" contact field. */
  push: {
    enabled: boolean;
    written: number;
    waiting: number;
    failing: number;
    lastError: string | null;
    missingField: { id: string; name: string }[];
    lastRun: { at: number; written: number; failed: number; error: string | null } | null;
  };
  running: boolean;
  rated: number;
  averageScore: number | null;
  ratedSevenPlus: number;
  waiting: number;
  ratedLast24h: number;
  lastRun: { at: number; rated: number; failed: number; error: string | null; trigger: string } | null;
}

// --- Logs ------------------------------------------------------------------
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEvent {
  id: number;
  ts: number;
  level: LogLevel;
  category: string;
  message: string;
  locationId: string | null;
  locationName: string | null;
  runId: number | null;
  data: Record<string, unknown> | null;
}

export interface SyncRun {
  id: number;
  startedAt: number;
  finishedAt: number | null;
  trigger: string;
  lane: string | null;
  status: 'running' | 'ok' | 'errors';
  locations: number;
  skipped: number;
  calls: number;
  appointments: number;
  opportunities: number;
  opportunitySweepsSkipped: number;
  activity: number;
  contacts: number;
  recordings: number;
  requests: number;
  requestFailures: number;
  rateLimited: number;
  errors: { locationId: string; message: string }[];
  enrichmentFinishedAt: number | null;
}

export interface LogSummary {
  last24h: { info: number; warn: number; error: number };
  categories: string[];
  lastRun: { id: number; startedAt: number; finishedAt: number | null; status: string } | null;
}

export interface LogFilterState {
  level: '' | LogLevel;
  categories: string[];
  locationId: string;
  runId: string;
  q: string;
  /** Hours back; 0 for everything kept. */
  hours: number;
}

// --- Recordings (Files tab) -----------------------------------------------
export interface StoredRecordingRow {
  callId: string;
  locationId: string;
  locationName: string | null;
  contactId: string | null;
  contactName: string | null;
  contactPhone: string | null;
  userId: string | null;
  userName: string | null;
  direction: string | null;
  outcome: string | null;
  durationSeconds: number;
  callAt: number;
  bytes: number;
  contentType: string | null;
  storedAt: number;
}

export interface RecordingsResponse {
  total: number;
  bytes: number;
  seconds: number;
  recordings: StoredRecordingRow[];
}

export interface RecordingsSummary {
  minSeconds: number;
  compacting: boolean;
  compaction: {
    running: boolean;
    startedAt: number;
    finishedAt: number | null;
    beforeBytes: number;
    afterBytes: number | null;
    error: string | null;
  } | null;
  disk: { totalBytes: number; freeBytes: number } | null;
  files: { recordingsBytes: number; databaseBytes: number; logsBytes: number };
  archive?: { stored: number; bytes: number; missing: number; failing: number; deleted: number; waiting: number };
  short?: { count: number; bytes: number };
  reclaimableBytes?: number;
  incrementalVacuum?: boolean;
}

export interface RecordingQuery {
  locationId: string;
  q: string;
  length: 'all' | 'short' | '1-5' | '5-15' | '15+';
  sort: 'date' | 'length' | 'size' | 'subaccount' | 'contact';
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

// --- Saved views -----------------------------------------------------------
export type ViewPage = 'calls' | 'appointments' | 'opportunities';

export interface SavedView {
  id: string;
  page: ViewPage;
  name: string;
  filters: Record<string, unknown>;
  isDefault: boolean;
  createdAt: number;
  updatedAt: number;
}

// --- AI ----------------------------------------------------------------------
export type AiProviderId = 'anthropic' | 'openai' | 'gemini';

export interface AiProviderStatus {
  id: AiProviderId;
  label: string;
  defaultModel: string;
  suggestedModels: string[];
  keyUrl: string;
  keyPlaceholder: string;
  configured: boolean;
  keyHint: string | null;
  model: string | null;
  updatedAt: number | null;
  lastVerifiedAt: number | null;
  lastError: string | null;
}

export interface AiSettingsResponse {
  activeProvider: AiProviderId | null;
  businessContext: string;
  include: { messages: boolean; notes: boolean; transcripts: boolean };
  ready: boolean;
  providers: AiProviderStatus[];
  rating: { enabled: boolean; windowDays: number; dailyLimit: number; model: string; prompt: string; pushToGhl: boolean };
  usage30d?: {
    kind: 'summary' | 'rating' | 'assistant';
    provider: string;
    model: string;
    requests: number;
    items: number;
    inputTokens: number;
    outputTokens: number;
  }[];
  models?: string[];
}

export interface LocationAiSummary {
  headline: string;
  summary: string;
  highlights: string[];
  concerns: string[];
  actions: string[];
}

export interface LeadAiSummary {
  headline: string;
  summary: string;
  temperature: 'hot' | 'warm' | 'cold' | 'unknown';
  stage: string;
  keyFacts: string[];
  concerns: string[];
  nextSteps: string[];
}

export interface LeadSummarySources {
  calls: number;
  transcripts: number;
  messages: number;
  emails: number;
  notes: number;
  appointments: number;
  opportunities: number;
  unavailable: string[];
  excluded: string[];
}

export interface AiSummaryRecord<T> {
  id: number;
  scope: 'location' | 'contact';
  scopeId: string;
  period: string;
  content: T;
  sources: unknown;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
  createdAt: number;
}

/** The Overview tab's filters: one range and set of sub-accounts for all three reports. */
export interface OverviewFilterState {
  from: string;
  to: string;
  locationIds: string[];
  activeClientsOnly: boolean;
}

export interface OverviewResponse {
  range: { from: number; to: number };
  calls: { total: number; answered: number; talkSeconds: number; contacts: number; previous: { total: number; answered: number } };
  appointments: {
    total: number;
    showed: number;
    noShows: number;
    cancelled: number;
    upcoming: number;
    previous: { total: number; showed: number };
  };
  opportunities: {
    total: number;
    won: number;
    open: number;
    lost: number;
    wonValue: number;
    openValue: number;
    previous: { total: number; won: number; wonValue: number };
  };
  timeseries: { day: string; calls: number; appointments: number; opportunities: number }[];
  byLocation: {
    locationId: string;
    locationName: string | null;
    calls: number;
    answered: number;
    talkSeconds: number;
    appointments: number;
    showed: number;
    noShows: number;
    opportunities: number;
    won: number;
    wonValue: number;
  }[];
}

/** A sub-account's own AI setup; empty strings mean "use the default". */
export interface LocationAiProfile {
  locationId: string;
  businessContext: string;
  ratingPrompt: string;
  updatedAt: number | null;
  updatedBy: string | null;
  /** What the sub-account uses while `ratingPrompt` is empty. */
  defaultRatingPrompt: string;
}

/** Settings → General → Time zone. */
export interface TimeZoneSettings {
  /** IANA zone; '' = each person's browser. */
  defaultZone: string;
  /** A single selected sub-account shows in its own zone. */
  followSubaccount: boolean;
}

/** A check for contacts deleted in HighLevel. */
export interface DeletedCheckStatus {
  running: boolean;
  startedAt: number;
  finishedAt: number | null;
  liveContacts: number;
  storedContacts: number;
  candidates: number;
  checked: number;
  found: number;
  error: string | null;
}

export interface DeletedContact {
  contactId: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  detectedAt: number;
  /** When its deals disappeared from HighLevel, if it had any. */
  dealDeletedAt: number | null;
  /** Best estimate of the deletion: the deals' date, else when a check found it gone. */
  deletedAt: number;
  deletedFrom: 'deals' | 'check';
  rating: number | null;
  calls: number;
  firstCallAt: number | null;
  lastCallAt: number | null;
  lastAppointmentAt: number | null;
  deals: number;
  tags: string | null;
}
